import '../Article.css';
import { useParams, useLocation } from 'react-router';
import { Stack, Box, useMediaQuery, useTheme, Divider } from '@mui/material';
import ArticleHeader from '../components/Article/ArticleHeader';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../components/Auth/AuthProvider';
import TableOfContentsDrawer from '../components/Article/TableOfContentsDrawer';
import ArticleSkeleton from '../components/Article/ArticleSkeleton';
import ErrorView from '../components/ErrorViews/ErrorView';
import {
  ArticleContext,
  ArticleContextInterface,
} from '../components/Article/ArticleContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import ArticleInformationDrawer from '../components/Article/ArticleInformationDrawer';
import { apiStates, CONTENT_TYPE } from '../types/types';
import { useArticle } from '../hooks/useArticle';
import ArticleMetaComponent from '../components/MetaData/ArticleMetaComponent';
import ArticleFooterArea from '../features/Articles/components/ArticleFooterArea';
import PublicArticleJaaAd from '../features/Articles/components/PublicArticleInfoSection';
import ArticlePreview from '../features/Reading/components/ArticlePreview';
import ArticleFullView from '../features/Reading/components/ArticleFullView';
import { getArticle } from '../api/api';
import { useQueryClient } from '@tanstack/react-query';
import ArticleHeaderSkeleton from '../components/Article/ArticleHeaderSkeleton';
import { hasSubscriptionAccess } from '../features/Authentication/utils/AuthUtilities';

export default function ArticlePage() {
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
  const { authInfo, actions, loading } = useContext(AuthContext);
  const { setArticle, article } = useContext(
    ArticleContext,
  ) as ArticleContextInterface;
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  /* within SM- and LG-breakpoints, the Table of Contents drawer width is
  compensated by adding padding to the article content area. */
  const compensateDrawerWidth = useMediaQuery(theme.breakpoints.up('sm'));
  const queryClient = useQueryClient();

  const {
    state: apiState,
    article: articleData,
    restricted,
    isUpcoming,
    apiError,
    isRefetching,
  } = useArticle(id, actions, loading);
  const location = useLocation();
  const toggleDrawer = (openDrawer: boolean) => () => {
    setOpen(openDrawer);
  };

  useEffect(() => {
    const { hash } = location;
    if (hash) {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        element.scrollIntoView();
      }
    }
  }, [location]);

  useEffect(() => {
    setArticle(articleData);
  }, [articleData, setArticle]);

  // Update document title when article is loaded
  const documentTitle = useMemo(() => {
    if (article?.meta.title) {
      return article.meta.title;
    }

    return 'Artikkeli';
  }, [article]);
  useDocumentTitle(documentTitle);

  // Prefetch on component mount (For search engines)
  useEffect(() => {
    const prefetchData = async () => {
      if (id) {
        const token = await actions?.getAccessToken();
        queryClient.prefetchQuery({
          queryKey: ['article', id],
          queryFn: () => getArticle(id, token),
        });
      }
    };
    prefetchData();
  }, [id, queryClient, actions]);

  if (apiState === apiStates.HAS_ERROR) {
    return <ErrorView apiError={apiError} />;
  }
  const isAccessResolved =
    !loading && restricted !== null && authInfo !== undefined;
  const isReadyForRendering =
    !loading && article !== null && restricted !== null;
  const showFullArticle = isReadyForRendering && !restricted && !isUpcoming;
  const addPadding =
    compensateDrawerWidth &&
    (!article || article.meta.type !== CONTENT_TYPE.LegalNews);

  return (
    <Stack direction='row' sx={{ position: 'relative' }}>
      <Stack
        direction='row'
        sx={{
          justifyContent: 'center',
          width: '100%',
          paddingTop: { xs: 3, sm: 6 },
          paddingBottom: { xs: 6, md: 12 },
          paddingRight: addPadding ? `${49}px` : 0,
        }}
      >
        <Box
          sx={{
            maxWidth: '900px',
            width: '100%',
            paddingX: { xs: 2, sm: 6, md: 8 },
          }}
        >
          {/* ARTICLE HEADER */}
          <Box sx={{ width: '100%' }}>
            {!isReadyForRendering && <ArticleHeaderSkeleton />}
            {isReadyForRendering && (
              <ArticleHeader
                meta={article.meta}
                restricted={
                  !hasSubscriptionAccess(authInfo) && !article.meta.isPublic
                }
                setIsInfoDrawerOpen={setIsInfoDrawerOpen}
                toggleTableOfContentsDrawer={toggleDrawer}
              />
            )}
          </Box>

          {!isReadyForRendering && <ArticleSkeleton />}
          {isReadyForRendering && (
            <Box>
              {!showFullArticle && article && (
                <ArticlePreview
                  article={article}
                  authInfo={authInfo}
                  restrictionResolved={isAccessResolved}
                  isRefetching={isRefetching}
                />
              )}
              {showFullArticle && article && (
                <ArticleFullView article={article} />
              )}

              <Stack sx={{ paddingTop: 6 }}>
                <Divider />
              </Stack>

              {/* ARTICLE FOOTER */}
              {article && <ArticleFooterArea article={article} />}

              {/* PROMOTIONAL SECTION */}
              {!hasSubscriptionAccess(authInfo) && (
                <>
                  <Stack sx={{ paddingTop: 3 }}>
                    <Divider />
                  </Stack>
                  <Box sx={{ marginTop: 6 }}>
                    <PublicArticleJaaAd />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Stack>

      {isReadyForRendering &&
        article &&
        article.meta.type !== CONTENT_TYPE.LegalNews && (
          <TableOfContentsDrawer
            tableOfContents={article.content.tableOfContents}
            title={article.meta.title}
            authors={article.meta.authors}
            closeDrawer={toggleDrawer(false)}
            openDrawer={toggleDrawer(true)}
            open={open}
            hasFootnotes={article.content.footnotes.length > 0}
          />
        )}

      {isMobileScreen && article && (
        <ArticleInformationDrawer
          meta={article.meta}
          closeDrawer={() => setIsInfoDrawerOpen(false)}
          openDrawer={() => setIsInfoDrawerOpen(true)}
          open={isInfoDrawerOpen}
        />
      )}

      <Fragment>
        {article?.meta && <ArticleMetaComponent meta={article.meta} />}
      </Fragment>
    </Stack>
  );
}
